<script setup lang="ts">
/**
 * 属性面板（对齐参考产品的分区顺序）：
 * 标识行 · 文字 · 填充 · 描边(色/宽) · 线型 · 位置与尺寸 · 旋转 · 不透明度 · 品牌色板
 *
 * M0 里**真正能改**的是：填充 / 描边 / 线宽 / 线型 / 不透明度；
 * 其余分区按实际能力置灰并标明里程碑（不摆假控件）。
 */
import { computed, ref } from 'vue';
import { useEditor } from '@/composables/useEditor';
import { setDash, setFill, setOpacity, setStroke, setStrokeWidth } from '@/core/ops/style';
import type { GeometryTarget } from '@/core/ops/geometry';
import { type TextAnchor } from '@/core/query/text';
import { DASH_LABEL, type DashKind } from '@/core/query/props';
import { setInspectorTab } from '@/stores/editor';
import FontField from './fields/FontField.vue';
import PaintField from './fields/PaintField.vue';
import SegField from './fields/SegField.vue';
import SliderField from './fields/SliderField.vue';

const editor = useEditor();
const p = computed(() => editor.props.value);

const DASH_OPTIONS: ReadonlyArray<{ value: DashKind; label: string }> = (
  ['solid', 'dashed', 'dotted', 'dashdot'] as DashKind[]
).map((value) => ({ value, label: DASH_LABEL[value] }));

const run = (cmd: ReturnType<typeof setFill>): void => {
  editor.execute(cmd);
};
const root = (): SVGSVGElement | null => editor.svg.value;
const sel = (): Element[] => editor.selection.value;

const applyFill = (v: string | null): void => {
  const r = root();
  if (r) run(setFill(r, sel(), v, { mergeKey: 'fill' }));
};
const applyStroke = (v: string | null): void => {
  const r = root();
  if (r) run(setStroke(r, sel(), v, { mergeKey: 'stroke' }));
};
const applyWidth = (e: Event): void => {
  const r = root();
  const n = Number((e.target as HTMLInputElement).value);
  if (r && Number.isFinite(n) && n >= 0) run(setStrokeWidth(r, sel(), n, { mergeKey: 'stroke-width' }));
};
const applyDash = (v: string): void => {
  const r = root();
  if (r) run(setDash(r, sel(), v as DashKind));
};
const applyOpacity = (v: number): void => {
  const r = root();
  if (r) run(setOpacity(r, sel(), v / 100, { mergeKey: 'opacity' }));
};

const fmt = (n: number | undefined): string => (n === undefined ? '' : String(Math.round(n)));

/** 数值输入 → 几何 op（X/Y/宽/高 各改一项；宽高有下限保护） */
const onGeom = (key: keyof GeometryTarget, e: Event): void => {
  const v = Number((e.target as HTMLInputElement).value);
  if (!Number.isFinite(v)) return;
  const target = { [key]: key === 'w' || key === 'h' ? Math.max(0.1, v) : v } as GeometryTarget;
  editor.setSelectionGeometry(target);
};

const applyRotation = (deg: number): void => {
  editor.setSelectionRotation(deg);
};
const onRotationInput = (e: Event): void => {
  const v = Number((e.target as HTMLInputElement).value);
  if (Number.isFinite(v)) applyRotation(v);
};
const hasBox = computed(() => p.value?.box != null);

/* ------------------------------------------------------------------ 位置锁
 * 文档原有图元的位置不可改（仍可选中、改颜色/文字）；本次新建的元素不受限。
 * 判据在 engine 侧（editor.canMoveSelection），这里只做禁用与新解锁入口。
 */
const posLocked = computed(() => editor.geometryLocked.value);
const canMove = computed(() => editor.canMoveSelection.value);
const togglePosLock = (): void => {
  editor.setGeometryLocked(!posLocked.value);
};

// ------------------------------------------------------------------ 圆角（rect）
const applyRadius = (value: number): void => {
  editor.setSelectionRadius(value);
};
const onRadiusInput = (e: Event): void => {
  const v = Number((e.target as HTMLInputElement).value);
  if (Number.isFinite(v) && v >= 0) applyRadius(v);
};

// ------------------------------------------------------------------ 文字
const tp = computed(() => editor.textProps.value);
const isHidden = computed(() => p.value?.hidden === true);
const ANCHORS: ReadonlyArray<{ value: TextAnchor; label: string }> = [
  { value: 'start', label: '左' },
  { value: 'middle', label: '中' },
  { value: 'end', label: '右' },
];

/**
 * 内容框**边打边生效**：每次 input 都写进文档。
 *
 * 两个守卫：
 *  · 中文输入法**组合期不提交**（否则打拼音的中间态会被写进文档，看着像乱码闪烁）；
 *    组合结束（compositionend）时再提交一次最终值。
 *  · 整段输入靠 `mergeKey` 合并成一条 undo —— 打 10 个字不该占 10 条历史。
 */
const composing = ref(false);
const onTextContent = (e: Event): void => {
  if (composing.value) return;
  editor.setTextContent((e.target as HTMLTextAreaElement).value, { mergeKey: 'text-content' });
};
const onCompositionEnd = (e: Event): void => {
  composing.value = false;
  onTextContent(e); // 提交组合后的最终值（浏览器已把它写进 textarea）
};
const setFont = (value: string): void => {
  editor.setTextStyle({ fontFamily: value });
};
// 自定义字体栈入口：暂时隐藏（见模板里那段注释）；要放出来就把这里和模板一起取消注释
// const onFontFamily = (e: Event): void => {
//   editor.setTextStyle({ fontFamily: (e.target as HTMLInputElement).value });
// };
const onFontSize = (e: Event): void => {
  const n = Number((e.target as HTMLInputElement).value);
  if (Number.isFinite(n) && n > 0) editor.setTextStyle({ fontSize: n });
};
const onLetterSpacing = (e: Event): void => {
  const n = Number((e.target as HTMLInputElement).value);
  if (Number.isFinite(n)) editor.setTextStyle({ letterSpacing: n });
};
const toggleBold = (): void => {
  editor.setTextStyle({ fontWeight: tp.value?.fontWeight === 'bold' ? 'normal' : 'bold' });
};
const toggleItalic = (): void => {
  editor.setTextStyle({ fontStyle: tp.value?.fontStyle === 'italic' ? 'normal' : 'italic' });
};

// 模板里的 inline 表达式会被 vue-tsc 按 void 校验，这里统一包一层（丢弃返回值）
const doVertical = (): void => {
  editor.toggleVertical();
};
const doHidden = (): void => {
  editor.toggleHidden();
};
const doDelete = (): void => {
  editor.deleteSelection();
};
const doEditText = (): void => {
  editor.beginTextEdit();
};
const setAnchor = (value: TextAnchor): void => {
  editor.setTextStyle({ textAnchor: value });
};
const opacityPercent = computed(() => Math.round((p.value?.opacity ?? 1) * 100));
</script>

<template>
  <div v-if="!p || p.count === 0" class="empty-hint">
    未选中元素<br />
    点击左侧列表或画布图元开始编辑
  </div>

  <div v-else>
    <div class="sec">
      <div class="sec-head">
        <span>{{ p.tag }}</span>
        <span class="grow" />
        <span class="chip">{{ p.id ? `#${p.id}` : '无 id' }}</span>
        <button class="ghost-btn" :title="isHidden ? '显示元素' : '隐藏元素'" @click="doHidden">
          {{ isHidden ? '显示' : '隐藏' }}
        </button>
        <button class="ghost-btn is-danger" title="删除元素 Delete" @click="doDelete">删除</button>
      </div>
      <div v-if="isHidden" class="panel-note">该元素已隐藏（display="none"），仍可从图层列表选中</div>
      <div v-if="p.count > 1" class="panel-note">已选 {{ p.count }} 个元素，批量修改</div>
    </div>

    <div v-if="p.supports.text && tp" class="sec">
      <div class="sec-head">
        <span>文字</span>
        <span class="grow" />
        <span class="panel-note">{{ editor.textEditing.value ? '编辑中…' : '画布上双击就地编辑' }}</span>
      </div>
      <textarea
        class="text-area"
        data-field="text-content"
        rows="2"
        spellcheck="false"
        :value="tp.content"
        @input="onTextContent"
        @compositionstart="composing = true"
        @compositionend="onCompositionEnd"
      />

      <div class="field">
        <label>字体</label>
        <FontField :value="tp.fontFamily" @change="setFont" />
      </div>
      <!-- 自定义字体栈入口：暂时隐藏（字体清单定稿前不开这个口子）。要放出来就取消这段注释，
           同时把下面脚本里的 onFontFamily 一起放出来 -->
      <!--
      <div class="field">
        <label>自定义</label>
        <div class="input"><input data-field="font-family-custom" :value="tp.fontFamily" @change="onFontFamily" /></div>
      </div>
      -->

      <div class="field">
        <label>字号</label>
        <div class="input"><input data-field="font-size" type="number" min="1" step="1" :value="tp.fontSize" @change="onFontSize" /></div>
        <label>字距</label>
        <div class="input"><input data-field="letter-spacing" type="number" step="0.5" :value="tp.letterSpacing" @change="onLetterSpacing" /></div>
      </div>

      <div class="field">
        <span class="panel-note">样式</span>
        <div class="seg">
          <button :class="{ 'is-active': tp.fontWeight === 'bold' }" title="粗体" @click="toggleBold">粗</button>
          <button :class="{ 'is-active': tp.fontStyle === 'italic' }" title="斜体" @click="toggleItalic">斜</button>
          <button
            data-field="vertical"
            :class="{ 'is-active': tp.vertical }"
            title="竖排（逐字 tspan）"
            @click="doVertical"
          >
            竖排
          </button>
        </div>
      </div>

      <div class="field">
        <label>对齐</label>
        <div class="seg">
          <button
            v-for="a in ANCHORS"
            :key="a.value"
            :class="{ 'is-active': tp.textAnchor === a.value }"
            @click="setAnchor(a.value)"
          >
            {{ a.label }}
          </button>
        </div>
      </div>

      <div class="field">
        <div class="seg" style="width: 100%">
          <button style="flex: 1" @click="doEditText">在画布上编辑文字</button>
        </div>
      </div>
    </div>

    <div v-if="p.supports.fill" class="sec">
      <div class="sec-head"><span>填充</span><span class="grow" /><span class="chip">{{ p.fill ?? 'none' }}</span></div>
      <PaintField :value="p.fill" which="fill" allow-none @solid="applyFill" />
    </div>

    <div v-if="p.supports.stroke" class="sec">
      <div class="sec-head"><span>描边</span><span class="grow" /><span class="chip">{{ p.stroke ?? 'none' }}</span></div>
      <PaintField :value="p.stroke" which="stroke" allow-none @solid="applyStroke" />
      <div class="field">
        <label>宽度</label>
        <div class="input">
          <input
            type="number"
            min="0"
            step="0.5"
            :value="p.strokeWidth ?? 1"
            @change="applyWidth"
          />
        </div>
      </div>
    </div>

    <div class="sec">
      <div class="sec-head"><span>线型</span><span class="grow" /><span class="chip">{{ DASH_LABEL[p.dash ?? 'solid'] }}</span></div>
      <SegField :value="p.dash ?? 'solid'" :options="DASH_OPTIONS" @change="applyDash" />
    </div>

    <div class="sec">
      <div class="sec-head">
        <span>位置与尺寸</span>
        <span class="grow" />
        <span v-if="!hasBox" class="panel-note">需元素已渲染</span>
        <button
          class="lock-toggle"
          :class="{ 'is-on': posLocked }"
          data-field="pos-lock"
          :aria-pressed="posLocked"
          :title="
            posLocked
              ? '位置已锁定：文档原有图元的位置/尺寸/旋转都改不动（防止误改实验图）。点此解锁后，拖动 / 手柄 / 方向键 / 下方坐标输入才会生效。'
              : '位置可改：点此锁定，防止误拖。本次新建的元素（画的、粘贴的）不受锁定影响。'
          "
          @click="togglePosLock"
        >
          <svg class="lock-ico" viewBox="0 0 16 16" width="11" height="11" aria-hidden="true">
            <path
              :d="posLocked ? 'M5.2 7V4.6a2.8 2.8 0 0 1 5.6 0V7' : 'M5.2 7V4.6a2.8 2.8 0 0 1 5.6 0'"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            />
            <rect
              x="3.2"
              y="7"
              width="9.6"
              height="6.4"
              rx="1.6"
              fill="none"
              stroke="currentColor"
              stroke-width="1.4"
            />
          </svg>
          {{ posLocked ? '位置不可改' : '位置可改' }}
        </button>
      </div>
      <div class="field">
        <label>X</label>
        <div class="input"><input type="number" :value="fmt(p.box?.x)" :disabled="!hasBox || !canMove" @change="onGeom('x', $event)" /></div>
        <label>Y</label>
        <div class="input"><input type="number" :value="fmt(p.box?.y)" :disabled="!hasBox || !canMove" @change="onGeom('y', $event)" /></div>
      </div>
      <div class="field">
        <label>宽</label>
        <div class="input"><input type="number" min="0.1" step="1" :value="fmt(p.box?.w)" :disabled="!hasBox || !canMove" @change="onGeom('w', $event)" /></div>
        <label>高</label>
        <div class="input"><input type="number" min="0.1" step="1" :value="fmt(p.box?.h)" :disabled="!hasBox || !canMove" @change="onGeom('h', $event)" /></div>
      </div>
    </div>

    <div v-if="p.supports.corner" class="sec">
      <div class="sec-head"><span>圆角</span><span class="grow" /><span class="mono">{{ Math.round(p.radius ?? 0) }}</span></div>
      <div class="field">
        <SliderField name="radius" :value="Math.round(p.radius ?? 0)" :min="0" :max="40" @change="applyRadius" />
        <div class="input" style="flex: 0 0 72px">
          <input data-field="radius" type="number" min="0" :value="Math.round(p.radius ?? 0)" @change="onRadiusInput" />
        </div>
      </div>
    </div>

    <div class="sec">
      <div class="sec-head"><span>旋转</span><span class="grow" /><span class="mono">{{ Math.round(p.rotation) }}°</span></div>
      <div class="field">
        <SliderField name="rotation" :value="Math.round(p.rotation)" :min="-180" :max="180" :disabled="!canMove" @change="applyRotation" />
        <div class="input" style="flex: 0 0 72px"><input type="number" :value="Math.round(p.rotation)" :disabled="!canMove" @change="onRotationInput" /></div>
        <span class="panel-note">°</span>
      </div>
    </div>

    <div class="sec">
      <div class="sec-head"><span>不透明度</span><span class="grow" /><span class="mono">{{ opacityPercent }}%</span></div>
      <SliderField name="opacity" :value="opacityPercent" :min="0" :max="100" @change="applyOpacity" />
    </div>

    <div class="sec">
      <div class="brand-card">
        <span class="swatch" style="background: var(--accent-tint)" />
        <div>
          <b>全局品牌色板</b>
          <span>统一调整全图配色</span>
        </div>
      </div>
      <button class="btn-block" @click="setInspectorTab('palette')">打开色板面板</button>
    </div>
  </div>
</template>
